/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faFilePdf, faFileWord, faSpinner, faCheck, faTimes, faBrain, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import '../styles/ResumeUpload.css';

const ResumeUpload = ({ onResumeUploaded, onClose }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [processingStep, setProcessingStep] = useState('');
    const [confidenceInfo, setConfidenceInfo] = useState(null);
    const fileInputRef = useRef(null);
    const { user, isAuthenticated } = useAuth();
    const token = user?.token;
    
    // Debug authentication state
    console.log('Modern ResumeUpload - Auth state:', { user, isAuthenticated, token, hasToken: !!token });

    const allowedTypes = [
        'application/pdf', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
        'application/msword', 
        'text/plain',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/bmp',
        'image/tiff',
        'image/gif'
    ];
    const maxFileSize = 10 * 1024 * 1024; // 10MB (increased for modern service)

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = ['pdf', 'docx', 'doc', 'txt', 'jpg', 'jpeg', 'png', 'bmp', 'tiff', 'gif'];
        
        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
            setError('Please select a valid file type (PDF, DOCX, DOC, TXT, or images: JPG, PNG, BMP, TIFF, GIF)');
            setSelectedFile(null);
            return;
        }

        // Validate file size
        if (file.size > maxFileSize) {
            setError('File size must be less than 10MB');
            setSelectedFile(null);
            return;
        }

        setError('');
        setSelectedFile(file);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file) {
            const input = fileInputRef.current;
            input.files = event.dataTransfer.files;
            handleFileSelect({ target: { files: [file] } });
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const uploadResume = async () => {
        console.log('🚀 Modern Resume Upload - selectedFile:', selectedFile, 'token:', token, 'user:', user);
        
        if (!selectedFile || !token) {
            setError('Please select a file and ensure you are logged in.');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        setError('');
        setSuccess('');
                    setProcessingStep('Initializing AI analysis...');

        // Enhanced progress simulation for modern service
        const progressInterval = setInterval(() => {
            setUploadProgress(prev => {
                if (prev < 90) {
                    const newProgress = prev + 10;
                    // Update processing step based on progress
                    if (newProgress < 30) {
                        setProcessingStep('Uploading your resume...');
                    } else if (newProgress < 60) {
                        setProcessingStep('AI extracting skills, experience & education...');
                    } else if (newProgress < 90) {
                        setProcessingStep('Building your profile from insights...');
                    }
                    return newProgress;
                }
                return prev;
            });
        }, 200);

        console.log('🚀 Starting modern resume upload...', {
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            fileType: selectedFile.type,
            hasToken: !!token
        });

        try {
            const formData = new FormData();
            formData.append('resume', selectedFile);

            console.log('🚀 Sending request to modern service:', buildApiUrl('/api/modern-resumes/upload'));

            const response = await fetch(buildApiUrl('/api/modern-resumes/upload'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
                mode: 'cors'
            });

            console.log('🚀 Modern service response status:', response.status);
            console.log('🚀 Response headers:', Object.fromEntries(response.headers.entries()));

            if (!response.ok) {
                clearInterval(progressInterval);
                let errorMessage = 'Modern resume processing failed';
                let errorDetails = '';
                let errorData = null;
                
                try {
                    const responseText = await response.text();
                    console.log('❌ Raw error response:', responseText);
                    
                    try {
                        errorData = JSON.parse(responseText);
                        errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
                        errorDetails = errorData.details || '';
                        
                        console.error('❌ Upload failed:', {
                            status: response.status,
                            error: errorMessage,
                            details: errorDetails,
                            extractionFailed: errorData.extraction_failed,
                            debugInfo: errorData.debug_info,
                            fullResponse: errorData
                        });
                    } catch (parseError) {
                        console.error('❌ Failed to parse JSON error response:', parseError);
                        console.error('❌ Response text was:', responseText);
                        // Handle 504 Gateway Timeout and HTML error pages
                        if (response.status === 504 || responseText.includes('504') || responseText.includes('Gateway Time-out')) {
                            errorMessage = 'Request timed out';
                            errorDetails = 'The AI is taking longer than expected to process your resume. This can happen during peak usage. Please try again in a minute.';
                        } else if (response.status === 502 || response.status === 503) {
                            errorMessage = 'Service temporarily unavailable';
                            errorDetails = 'The server is busy. Please try again in a few minutes.';
                        } else {
                            errorMessage = `Server error (${response.status})`;
                            errorDetails = responseText.substring(0, 150).replace(/<[^>]*>/g, '').trim() || 'Please try again later.';
                        }
                    }
                } catch (e) {
                    errorMessage = `Modern service error: ${response.status} - ${response.statusText}`;
                    console.error('❌ Failed to read error response:', e);
                }
                
                // Show detailed error message
                const fullErrorMessage = errorDetails 
                    ? `${errorMessage}\n\n${errorDetails}` 
                    : errorMessage;
                throw new Error(fullErrorMessage);
            }

            const data = await response.json();
            console.log('🚀 Modern upload successful:', data);
            console.log('🔍 Modern Upload Response Data Structure:');
            console.log('  - resume_data:', data.resume_data);
            console.log('  - ai_recommendations:', data.ai_recommendations);
            console.log('  - processing_method:', data.processing_method);
            console.log('  - ai_processed:', data.ai_processed);

            const validation = data?.resume_data?.validation || {};
            const overallConfidence = validation.overall_confidence;
            const precisionConfidence = validation.precision_confidence;
            const needsReview = typeof precisionConfidence === 'number' && precisionConfidence < 100;
            setConfidenceInfo({
                overallConfidence,
                precisionConfidence,
                needsReview,
            });

            clearInterval(progressInterval);
            setUploadProgress(100);
            setProcessingStep('Analysis complete!');
            if (precisionConfidence === 100) {
                setSuccess('AI extracted your profile with high precision. You can still review and edit anytime.');
            } else if (typeof precisionConfidence === 'number') {
                setSuccess('AI extracted your profile. Some fields may need a quick review.');
            } else {
                setSuccess('Your profile has been intelligently extracted and is ready.');
            }

            // Brief delay to show success message
            setTimeout(() => {
                if (onResumeUploaded) {
                    console.log('🔍 Calling onResumeUploaded with modern data:', {
                        resume_data: data.resume_data,
                        ai_recommendations: data.ai_recommendations
                    });
                    onResumeUploaded(data.resume_data, data.ai_recommendations);
                }
            }, 500);

        } catch (error) {
            clearInterval(progressInterval);
            console.error('🚀 Modern upload error details:', {
                message: error.message,
                name: error.name,
                stack: error.stack,
                type: typeof error
            });
            
            // Format error message - split by \n for line breaks
            const errorLines = error.message.split('\n');
            const formattedError = errorLines.length > 1 
                ? errorLines.join('\n')  // Multi-line error from backend
                : `Modern AI processing failed: ${error.message}`;
            
            setError(formattedError);
            setIsUploading(false);
            setUploadProgress(0);
            setProcessingStep('');
        }
    };

    const removeFile = () => {
        setSelectedFile(null);
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const getFileIcon = (fileType) => {
        if (fileType.includes('pdf')) return faFilePdf;
        if (fileType.includes('word') || fileType.includes('document')) return faFileWord;
        return faFileWord;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="resume_upload_modal">
            <div className="resume_upload_content">
                <div className="resume_upload_header">
                    <div className="header_text">
                        <h2>
                            <FontAwesomeIcon icon={faBrain} className="header_ai_icon" />
                            AI Resume Intelligence
                        </h2>
                        <p className="header_subtitle">We extract your profile—you focus on landing the role</p>
                    </div>
                    <button className="close_btn" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} size="xs" />
                    </button>
                </div>

                <div className="resume_upload_body">
                    {/* Hidden file input */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.bmp,.tiff,.gif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain,image/jpeg,image/png,image/bmp,image/tiff,image/gif"
                        style={{ display: 'none' }}
                    />
                    
                    {!selectedFile ? (
                        <div 
                            className="file_drop_zone"
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                        >
                            <div className="drop_zone_content">
                                <FontAwesomeIcon icon={faBrain} className="upload_icon ai_pulse" />
                                <h3>Drop your resume here</h3>
                                <p>AI reads it and fills your profile—skills, experience, education</p>
                                <button 
                                    className="btn btn_primary"
                                    onClick={() => fileInputRef.current?.click()}
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={faUpload} />
                                    Browse files
                                </button>
                                <p className="file_types">PDF, DOCX, TXT • Images supported (we use AI vision)</p>
                            </div>
                        </div>
                    ) : (
                        <div className="selected_file_info">
                            <div className="file_preview">
                                <FontAwesomeIcon icon={getFileIcon(selectedFile.type)} className="file_icon" />
                                <div className="file_details">
                                    <h4>{selectedFile.name}</h4>
                                    <p>{formatFileSize(selectedFile.size)} • Ready for AI analysis</p>
                                </div>
                                <button className="remove_file_btn" onClick={removeFile} title="Remove file">
                                    <FontAwesomeIcon icon={faTimes} size="xs" />
                                </button>
                            </div>
                            
                            <button 
                                className="btn btn_primary upload_btn btn_ai"
                                onClick={uploadResume}
                                disabled={isUploading}
                            >
                                {isUploading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} className="spinner" />
                                        AI analyzing<span className="parsing_dots">...</span>
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faWandMagicSparkles} />
                                        Extract my profile with AI
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {error && (
                        <div className="error_message">
                            <FontAwesomeIcon icon={faTimes} />
                            <span>{error}</span>
                        </div>
                    )}

                    {success && (
                        <div className="success_message">
                            <FontAwesomeIcon icon={faCheck} />
                            <span>{success}</span>
                        </div>
                    )}

                    {confidenceInfo && (
                        <div className="confidence_badge">
                            <div className="confidence_row">
                                <span className="confidence_label">AI confidence:</span>
                                <span className="confidence_value">
                                    {typeof confidenceInfo.overallConfidence === 'number'
                                        ? `${confidenceInfo.overallConfidence}%`
                                        : 'N/A'}
                                </span>
                            </div>
                            <div className="confidence_row">
                                <span className="confidence_label">Precision (no unverified fields):</span>
                                <span className="confidence_value">
                                    {typeof confidenceInfo.precisionConfidence === 'number'
                                        ? `${confidenceInfo.precisionConfidence}%`
                                        : 'N/A'}
                                </span>
                            </div>
                            {confidenceInfo.needsReview && (
                                <div className="confidence_notice">
                                    Review suggested: AI found all data from your resume,
                                    but some sections may still benefit from a quick manual check.
                                </div>
                            )}
                        </div>
                    )}

                    {isUploading && (
                        <div className="upload_progress">
                            <div className="futuristic_loader">
                                <div className="loader_ring" />
                                <div className="loader_orb loader_orb_1" />
                                <div className="loader_orb loader_orb_2" />
                                <div className="loader_orb loader_orb_3" />
                                <div className="loader_orb loader_orb_4" />
                                <div className="loader_core" />
                            </div>
                            <div className="progress_bar_container">
                                <div className="progress_bar" style={{ width: `${uploadProgress}%` }}>
                                    <div className="progress_bar_glow" />
                                </div>
                            </div>
                            <span className="processing_step">{processingStep}</span>
                            <span className="progress_percentage">{uploadProgress}%</span>
                        </div>
                    )}
                </div>

                <div className="resume_upload_footer">
                    <button 
                        className="btn btn_secondary"
                        onClick={onClose}
                        disabled={isUploading}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResumeUpload;
